/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Injectable } from '@angular/core';
import {
  BulkUpdateRestService,
  Formula,
  FormulaPropagationMode,
  HousePlanListItemDto,
} from '@gh/builder-shared-data';
import { ID } from '@gh/common-shared-data';
import { DialogService } from '@gh/core-ui';
import { merge$ } from '@gh/rx';
import { defaultIfEmpty$, map$, filter$, switchMap$ } from '@gh/rx/operators';
import { isEmpty } from 'lodash';
import { firstValueFrom, Observable } from 'rxjs';
import {
  FormulaPropagationDialogConfig,
  formulaPropagationDialogRoute,
  FormulaPropagationHpListDialogConfig,
  formulaPropagationHpListDialogRoute,
} from '../../dialogs';
import { FormulaContext, getReadableFormulaExpression } from '../../formula';

export interface FormulaPropagationConfig {
  formulaContext: FormulaContext;
  newFormula?: Formula;
  oldFormula?: Formula;
  costCenterId?: ID;
  housePlanId?: ID;
  isComponent: boolean;
  elementId: ID;
  elementCode: string;
}

type FormulaPropagationHplistItem = HousePlanListItemDto;

/**
 * This service contains formula propagation logic.
 */
@Injectable()
export class FormulaPropagationService {

  constructor(private dialogs: DialogService,
              private bulkUpdateRest: BulkUpdateRestService) {

  }

  propagateFormula(config: FormulaPropagationConfig): Promise<void> {
    const { newFormula, oldFormula, elementId, isComponent, costCenterId, housePlanId } = config;

    return firstValueFrom(
      this.propagateFormulaDialog(config).pipe(
        switchMap$((list: FormulaPropagationHplistItem[]) => {
          const includesIds = list && list.map((hp) => hp.housePlanId) || [];
          const excludesIds = housePlanId ? [housePlanId] : [];
          const isExcludeMode = isEmpty(includesIds);
          return this.bulkUpdateRest.propagateFormula({
            newFormula,
            oldFormula,
            itemId: isComponent ? void 0 : elementId,
            componentId: isComponent ? elementId : void 0,
            costCenterId,
            targetHousePlans: {
              isExcludeMode,
              idList: isExcludeMode ? excludesIds : includesIds,
            },
          });
        }),
        defaultIfEmpty$(undefined),
      ),
    ).then(() => void 0);
  }

  private propagateFormulaDialog(config: FormulaPropagationConfig): Observable<FormulaPropagationHplistItem[]> {
    const {
      oldFormula, newFormula, elementId, elementCode, costCenterId, isComponent, formulaContext, housePlanId,
    } = config;
    const formulaStr = getReadableFormulaExpression(formulaContext, newFormula);
    const answer$ = this.dialogs.openWithInput(
      formulaPropagationDialogRoute,
      <FormulaPropagationDialogConfig>{
        formula: formulaStr,
        isComponent,
        elementCode,
      }).afterClosed().pipe(
      filter$(Boolean)); // you press Cancel button (only if dialog returns falsy value for this case)

    const propagateToAll$ = answer$.pipe(
      filter$((whereToPropagate: FormulaPropagationMode) => whereToPropagate === FormulaPropagationMode.All),
      map$(() => []));

    const propagateToSelected$ = answer$.pipe(
      filter$((whereToPropagate: FormulaPropagationMode) => whereToPropagate === FormulaPropagationMode.Selected),
      switchMap$(() => this.dialogs.openWithInput(
        formulaPropagationHpListDialogRoute,
        <FormulaPropagationHpListDialogConfig>{
          formula: formulaStr,
          oldFormula,
          costCenterId,
          elementId,
          elementCode,
          isComponent,
          excludeIds: housePlanId ? [housePlanId] : void 0,
        }).afterClosed().pipe(
        filter$(Boolean))));
    return merge$(propagateToAll$, propagateToSelected$);
  }
}
